import { FC, Fragment, type KeyboardEvent, useMemo, useRef, useState } from 'react';

import type { TeamActivityDay, TeamActivityMember } from '@/api/gamma-team/types';
import { theme, Typography } from '@/design-system';

import { HEAT_COLORS } from './chart-palette';
import {
  ChartCard,
  ChartHeader,
  ChartTooltipBox,
  HeatCell,
  HeatmapFrame,
  HeatmapGrid,
  HeatmapScroller,
  HeatmapTooltip,
  HeatNameButton,
  LegendItem,
  LegendList,
  SectionTitle,
  SkeletonBar,
  TooltipRow,
} from './styled';
import { memberName } from './team-sorting';
import {
  bucketMemberActivity,
  type Granularity,
  HEAT_LEVELS,
  heatLevel,
  toBuckets,
} from './team-trends';

export const HEATMAP_TITLE = 'Activity by member';
export const HEATMAP_MAX_ROWS = 20;

const LABEL_EVERY = { day: 7, week: 2 } as const;
const TOOLTIP_WIDTH = 220;
const TOOLTIP_GAP = theme.spacing[8];

const activityLabel = (count: number): string =>
  `${count.toLocaleString()} ${count === 1 ? 'activity' : 'activities'}`;

interface HoveredCell {
  row: number;
  column: number;
  left: number;
  top: number;
}

interface CellPosition {
  row: number;
  column: number;
}

const cellKey = ({ row, column }: CellPosition): string => `${row}:${column}`;

const KEY_MOVES: Record<
  string,
  (_at: CellPosition, _rows: number, _columns: number) => CellPosition
> = {
  ArrowLeft: ({ row, column }) => ({ row, column: Math.max(0, column - 1) }),
  ArrowRight: ({ row, column }, _rows, columns) => ({
    row,
    column: Math.min(columns - 1, column + 1),
  }),
  ArrowUp: ({ row, column }) => ({ row: Math.max(0, row - 1), column }),
  ArrowDown: ({ row, column }, rows) => ({ row: Math.min(rows - 1, row + 1), column }),
  Home: ({ row }) => ({ row, column: 0 }),
  End: ({ row }, _rows, columns) => ({ row, column: columns - 1 }),
};

interface ActivityHeatmapProps {
  daily: TeamActivityDay[] | null;
  members: TeamActivityMember[] | null;
  granularity: Granularity;
  onSelectMember: (_userId: string) => void;
}

export const ActivityHeatmap: FC<ActivityHeatmapProps> = ({
  daily,
  members,
  granularity,
  onSelectMember,
}) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<HoveredCell | null>(null);
  const [focusable, setFocusable] = useState<CellPosition>({ row: 0, column: 0 });
  const cells = useRef(new Map<string, HTMLElement>());

  const model = useMemo(() => {
    if (!daily || !members) return null;
    const buckets = toBuckets(daily, granularity);
    const rows = members
      .filter((member) => member.dailyActivity)
      .map((member) => {
        const values = bucketMemberActivity(daily, member.dailyActivity ?? [], granularity);
        return { member, values, total: values.reduce((sum, value) => sum + value, 0) };
      })
      .filter(({ total }) => total > 0)
      .sort(
        (a, b) => b.total - a.total || memberName(a.member).localeCompare(memberName(b.member)),
      );
    const max = Math.max(0, ...rows.flatMap(({ values }) => values));
    return {
      buckets,
      rows: rows.slice(0, HEATMAP_MAX_ROWS),
      hidden: Math.max(0, rows.length - HEATMAP_MAX_ROWS),
      max,
    };
  }, [daily, members, granularity]);

  if (members && !members.some((member) => member.dailyActivity)) return null;

  const showCell = (target: HTMLElement, row: number, column: number) => {
    const frame = frameRef.current?.getBoundingClientRect();
    const cell = target.getBoundingClientRect();
    if (!frame) return;
    const centre = cell.left - frame.left + cell.width / 2;
    const left = Math.min(
      Math.max(centre - TOOLTIP_WIDTH / 2, 0),
      Math.max(0, frame.width - TOOLTIP_WIDTH),
    );
    setHovered({ row, column, left, top: cell.bottom - frame.top + TOOLTIP_GAP });
  };

  const moveFocus = (event: KeyboardEvent<HTMLElement>, at: CellPosition, userId: string) => {
    if (!model) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelectMember(userId);
      return;
    }
    const move = KEY_MOVES[event.key];
    if (!move) return;
    event.preventDefault();
    const next = move(at, model.rows.length, model.buckets.length);
    setFocusable(next);
    cells.current.get(cellKey(next))?.focus();
  };

  const rowCount = model?.rows.length ?? 0;
  const columnCount = model?.buckets.length ?? 0;
  const tabStop =
    focusable.row < rowCount && focusable.column < columnCount ? focusable : { row: 0, column: 0 };

  const hoveredRow = hovered && model ? model.rows[hovered.row] : null;
  const hoveredBucket = hovered && model ? model.buckets[hovered.column] : null;
  const hoveredValue = hoveredRow && hovered ? (hoveredRow.values[hovered.column] ?? 0) : 0;

  return (
    <ChartCard aria-label={HEATMAP_TITLE}>
      <ChartHeader>
        <SectionTitle>
          <Typography variant="body1" fontWeight={600}>
            {HEATMAP_TITLE}
          </Typography>
          <Typography variant="body4" color={theme.colors.text.secondary}>
            Entries and deal moves per {granularity}; gaps show who went quiet and when
          </Typography>
        </SectionTitle>
        <LegendList aria-label="Colour scale">
          <LegendItem>
            <Typography variant="body4" color={theme.colors.text.secondary}>
              Less
            </Typography>
          </LegendItem>
          {HEAT_COLORS.map((color, level) => (
            <LegendItem
              key={color}
              aria-label={level === 0 ? 'None' : `Level ${level} of ${HEAT_LEVELS}`}
            >
              <HeatCell color={color} style={{ width: theme.spacing[12] }} />
            </LegendItem>
          ))}
          <LegendItem>
            <Typography variant="body4" color={theme.colors.text.secondary}>
              More
            </Typography>
          </LegendItem>
        </LegendList>
      </ChartHeader>

      {!model ? (
        <SkeletonBar h={160} />
      ) : model.rows.length === 0 ? (
        <Typography variant="body3" color={theme.colors.text.secondary}>
          No one logged activity in this period.
        </Typography>
      ) : (
        <HeatmapFrame ref={frameRef} onMouseLeave={() => setHovered(null)}>
          <HeatmapScroller onScroll={() => setHovered(null)}>
            <HeatmapGrid columns={model.buckets.length} role="table" aria-label={HEATMAP_TITLE}>
              <span role="columnheader" />
              {model.buckets.map((bucket, index) => (
                <Typography
                  key={bucket.key}
                  role="columnheader"
                  variant="body4"
                  color={
                    hovered?.column === index
                      ? theme.colors.text.primary
                      : theme.colors.text.tertiary
                  }
                  noWrap
                  style={{ overflow: 'visible' }}
                  aria-label={bucket.label}
                >
                  {index % LABEL_EVERY[granularity] === 0 || hovered?.column === index
                    ? bucket.label.replace('Week of ', '')
                    : ''}
                </Typography>
              ))}
              {model.rows.map(({ member, values }, row) => {
                const name = memberName(member);
                return (
                  <Fragment key={member.userId}>
                    <HeatNameButton
                      type="button"
                      role="rowheader"
                      title={name}
                      data-active={hovered?.row === row || undefined}
                      onClick={() => onSelectMember(member.userId)}
                    >
                      {name}
                    </HeatNameButton>
                    {values.map((value, column) => {
                      const at = { row, column };
                      const inCross = hovered?.row === row || hovered?.column === column;
                      return (
                        <HeatCell
                          key={model.buckets[column]?.key ?? column}
                          ref={(node: HTMLElement | null) => {
                            if (node) cells.current.set(cellKey(at), node);
                            else cells.current.delete(cellKey(at));
                          }}
                          role="cell"
                          tabIndex={cellKey(tabStop) === cellKey(at) ? 0 : -1}
                          color={HEAT_COLORS[heatLevel(value, model.max)]}
                          aria-label={`${name} · ${model.buckets[column]?.label}: ${activityLabel(value)}`}
                          data-active={
                            (hovered?.row === row && hovered.column === column) || undefined
                          }
                          data-dimmed={(hovered && !inCross) || undefined}
                          data-clickable
                          onMouseEnter={(event) => showCell(event.currentTarget, row, column)}
                          onFocus={(event) => {
                            setFocusable(at);
                            showCell(event.currentTarget, row, column);
                          }}
                          onBlur={() => setHovered(null)}
                          onKeyDown={(event) => moveFocus(event, at, member.userId)}
                          onClick={() => onSelectMember(member.userId)}
                        />
                      );
                    })}
                  </Fragment>
                );
              })}
            </HeatmapGrid>
          </HeatmapScroller>

          {hovered && hoveredRow && hoveredBucket && (
            <HeatmapTooltip
              role="tooltip"
              style={{ left: hovered.left, top: hovered.top, width: TOOLTIP_WIDTH }}
            >
              <ChartTooltipBox>
                <Typography variant="body4" color={theme.colors.text.secondary}>
                  {hoveredBucket.label}
                </Typography>
                <Typography
                  variant="body3"
                  fontWeight={600}
                  color={theme.colors.text.primary}
                  noWrap
                >
                  {memberName(hoveredRow.member)}
                </Typography>
                <TooltipRow>
                  <Typography variant="body4" color={theme.colors.text.secondary}>
                    Entries and deal moves
                  </Typography>
                  <Typography variant="body4" fontWeight={600} color={theme.colors.text.primary}>
                    {hoveredValue.toLocaleString()}
                  </Typography>
                </TooltipRow>
                <TooltipRow>
                  <Typography variant="body4" color={theme.colors.text.secondary}>
                    Whole team
                  </Typography>
                  <Typography variant="body4" color={theme.colors.text.primary}>
                    {(
                      hoveredBucket.entries +
                      hoveredBucket.dealsCreated +
                      hoveredBucket.dealsAdvanced
                    ).toLocaleString()}
                  </Typography>
                </TooltipRow>
                <Typography variant="body4" color={theme.colors.text.tertiary}>
                  Click or press Enter to open the member
                </Typography>
              </ChartTooltipBox>
            </HeatmapTooltip>
          )}

          {model.hidden > 0 && (
            <Typography variant="body4" color={theme.colors.text.tertiary}>
              {`${model.hidden} more members with lighter activity are listed in the table below.`}
            </Typography>
          )}
        </HeatmapFrame>
      )}
    </ChartCard>
  );
};
